import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star, X, Search, ChevronRight, Check, Minus } from 'lucide-react';
import { request } from '../api/client';
import { Pill } from './TwDataTable';

type Level = 'zone' | 'state' | 'district' | 'beat';

const LEVEL_PILL: Record<Level, string> = { zone: 'purple', state: 'blue', district: 'lime', beat: 'green' };
const LEVEL_LABEL: Record<Level, string> = { zone: 'Zone', state: 'State', district: 'District', beat: 'Beat' };
// Small dot used in column headers (the level capsule is dropped from rows to keep them readable).
const DOT: Record<Level, string> = { zone: 'bg-violet-500', state: 'bg-sky-500', district: 'bg-lime-500', beat: 'bg-brand-500' };

// Which levels each role may SELECT (checkbox). Others are navigation-only, so you can still drill
// through them to reach the levels you can pick. Mirrors the backend LEVELS_FOR_ROLE.
const CHECKABLE: Record<string, Level[]> = {
  SALES_PERSON: ['district', 'beat'],
  SALES_HEAD: ['state', 'district', 'beat'],
};

interface TerrNode { id: string; code: string; name: string; level: Level; children?: TerrNode[] }

/** The picker's value: the set of bound territory ids + the ⭐ owned set (territories an SP owns). */
export interface TerritorySelection { ids: string[]; primaryIds: string[] }

interface Props {
  value?: TerritorySelection;
  onChange?: (v: TerritorySelection) => void;
  /** Role code — decides which levels are checkable. */
  role: string;
  disabled?: boolean;
}

/**
 * `partial` = this node is NOT bound, but something inside it is. It reads as a dash rather than a
 * tick on purpose: ticking a state binds the WHOLE state, which is a broader posting than the one
 * district underneath it, so a full tick here would misstate what the person actually covers.
 */
function CheckBox({ checked, partial }: { checked: boolean; partial?: boolean }) {
  const box = checked
    ? 'border-brand-600 bg-brand-600 text-white'
    : partial
      ? 'border-brand-600 bg-brand-500/20 text-brand-700 dark:text-brand-300'
      : 'border-line-strong bg-surface';
  return (
    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${box}`}>
      {checked ? <Check className="h-3 w-3" strokeWidth={3} /> : partial ? <Minus className="h-3 w-3" strokeWidth={3} /> : null}
    </span>
  );
}

/**
 * Cascading (miller-columns) multi-select for territories, built with Tailwind. Click a node to
 * drill one level to the right (zone → state → district → beat); tick a checkbox to bind that node.
 * Selection is node-precise — checking a state does NOT check its districts. Levels above the role's
 * reach are navigation-only. When only ONE zone exists it is auto-entered and its column is skipped.
 * For a Sales Person the ⭐ marks EACH territory they own (new customers there route to them). A sole
 * coverer owns many, so more than one can be starred; un-star any they only cover. A Sales Head owns
 * only the territories he EXPLICITLY stars (any level — state/district/beat); un-star ones he only oversees.
 */
export function TerritoryPicker({ value, onChange, role, disabled }: Props) {
  const ids = value?.ids ?? [];
  const primaryIds = value?.primaryIds ?? [];
  const isSp = role === 'SALES_PERSON';
  const isSh = role === 'SALES_HEAD';
  // Who may ⭐-OWN a territory: a Sales Person, or a Sales Head — at any level he's bound to. A head
  // only ever owns what he EXPLICITLY stars; merely overseeing a district never auto-owns it.
  const canOwn = isSp || isSh;
  const checkable = CHECKABLE[role] ?? [];

  const { data: tree = [], isLoading } = useQuery<TerrNode[]>({
    queryKey: ['territories-tree-picker'],
    queryFn: () => request<TerrNode[]>({ url: '/territories/tree' }),
  });

  const { byId, parentOf, flat } = useMemo(() => {
    const byId = new Map<string, TerrNode>();
    const parentOf = new Map<string, string | null>();
    const flat: TerrNode[] = [];
    const walk = (n: TerrNode, parent: string | null) => {
      byId.set(String(n.id), n);
      parentOf.set(String(n.id), parent);
      flat.push(n);
      (n.children ?? []).forEach((c) => walk(c, String(n.id)));
    };
    tree.forEach((n) => walk(n, null));
    return { byId, parentOf, flat };
  }, [tree]);

  // Single-zone collapse: if the whole tree hangs off one zone, enter it and start at its states.
  const soleZone = tree.length === 1 && tree[0].level === 'zone' ? tree[0] : null;
  const baseNodes = soleZone ? (soleZone.children ?? []) : tree;

  const [trail, setTrail] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  // How many selected territories sit UNDER each node. Lets a COLLAPSED ancestor show that something
  // inside it is picked — without which an existing binding looks like nothing is selected at all,
  // because the tick lives several levels down behind a chevron.
  const selectedUnder = useMemo(() => {
    const m = new Map<string, number>();
    for (const id of ids) {
      let p = parentOf.get(String(id)) ?? null;
      while (p) { m.set(p, (m.get(p) ?? 0) + 1); p = parentOf.get(p) ?? null; }
    }
    return m;
  }, [ids, parentOf]);

  // Open the tree TO the existing selection the first time it arrives, so an edit form shows the
  // bound territory ticked instead of a collapsed root the reader has to go hunting through.
  const revealed = useRef(false);
  useEffect(() => {
    if (revealed.current || !ids.length || !byId.size) return;
    const chain: string[] = [];
    let p = parentOf.get(String(ids[0])) ?? null;
    while (p) { chain.unshift(p); p = parentOf.get(p) ?? null; }
    // The sole zone is collapsed away from the columns, so it is not a step in the trail.
    const path = soleZone ? chain.filter((c) => c !== String(soleZone.id)) : chain;
    if (path.length) setTrail(path);
    revealed.current = true;
  }, [ids, byId, parentOf, soleZone]);

  const columns = useMemo(() => {
    const cols: TerrNode[][] = [baseNodes];
    for (const id of trail) {
      const kids = byId.get(id)?.children ?? [];
      if (!kids.length) break;
      cols.push(kids);
    }
    return cols;
  }, [baseNodes, trail, byId]);

  const emit = (next: TerritorySelection) => onChange?.(next);

  const toggle = (id: string) => {
    if (disabled) return;
    const node = byId.get(id);
    if (!node || !checkable.includes(node.level)) return;
    if (ids.includes(id)) {
      // Remove from the selection AND from ownership.
      emit({ ids: ids.filter((x) => x !== id), primaryIds: primaryIds.filter((x) => x !== id) });
    } else {
      // Add. Whoever may own this level takes it by default (an SP's sole-coverer patch, or a beat a
      // Sales Head personally works) — un-star to just cover/oversee it.
      emit({ ids: [...ids, id], primaryIds: canOwn ? [...primaryIds, id] : primaryIds });
    }
  };

  // Toggle ownership of a single territory. More than one may be owned; a Sales Head may own beats only.
  const togglePrimary = (id: string) => {
    if (disabled) return;
    const node = byId.get(id);
    if (!node || !canOwn) return;
    emit({ ids, primaryIds: primaryIds.includes(id) ? primaryIds.filter((x) => x !== id) : [...primaryIds, id] });
  };

  const drill = (colIndex: number, node: TerrNode) => {
    const kids = node.children ?? [];
    if (kids.length) setTrail([...trail.slice(0, colIndex), String(node.id)]);
    else if (checkable.includes(node.level)) toggle(String(node.id)); // leaf you can pick → tick it
  };

  const results = query.trim()
    ? flat.filter((n) => checkable.includes(n.level) && `${n.code} ${n.name}`.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 24)
    : [];

  const ancestorLabel = (id: string): string => {
    const parts: string[] = [];
    let p = parentOf.get(id) ?? null;
    while (p) { const n = byId.get(p); if (n && n.level !== 'zone') parts.unshift(n.name); p = parentOf.get(p) ?? null; }
    return parts.join(' › ');
  };

  return (
    <div className="rounded-card border border-line bg-surface shadow-e1">
      {/* Search — jumps straight to any pickable district/beat without drilling. */}
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-subtle" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={isSp ? 'Search a district or beat…' : 'Search a state, district or beat…'}
          aria-label={isSp ? 'Search a district or beat' : 'Search a state, district or beat'}
          disabled={disabled}
          className="w-full bg-transparent text-sm text-content placeholder:text-subtle focus:outline-none"
        />
        {query && (
          <button type="button" onClick={() => setQuery('')} className="text-subtle hover:text-content"><X className="h-4 w-4" /></button>
        )}
      </div>

      {/* Body: search results, or the cascading columns. */}
      {isLoading ? (
        <div className="px-3 py-6 text-center text-sm text-subtle">Loading territories…</div>
      ) : query.trim() ? (
        <div className="max-h-72 overflow-y-auto p-1">
          {results.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-subtle">No matching territory</div>
          ) : results.map((n) => {
            const checked = ids.includes(String(n.id));
            return (
              <button
                key={n.id} type="button" disabled={disabled} onClick={() => toggle(String(n.id))}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-2 ${checked ? 'bg-brand-500/10' : ''}`}
              >
                <CheckBox checked={checked} />
                <Pill color={LEVEL_PILL[n.level]}>{LEVEL_LABEL[n.level]}</Pill>
                <span className="font-medium tabular-nums text-content">{n.code}</span>
                <span className="truncate text-subtle">· {n.name}</span>
                {ancestorLabel(String(n.id)) && <span className="ml-auto shrink-0 pl-2 text-xs text-muted">{ancestorLabel(String(n.id))}</span>}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex max-h-72 overflow-x-auto">
          {columns.map((nodes, ci) => {
            // Every row in a column shares one level, so the level lives in a sticky header (not
            // repeated on each row) — that's what keeps the columns narrow and readable.
            const colLevel = nodes[0]?.level;
            return (
              <div key={ci} className="w-52 shrink-0 overflow-y-auto border-r border-line last:border-r-0">
                {colLevel && (
                  <div className="sticky top-0 z-1 flex items-center gap-1.5 border-b border-line bg-surface-2 px-2.5 py-1">
                    <span className={`h-2 w-2 rounded-full ${DOT[colLevel]}`} />
                    <span className="type-overline text-subtle">{LEVEL_LABEL[colLevel]}</span>
                  </div>
                )}
                {nodes.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-subtle">No sub-territories</div>
                ) : nodes.map((n) => {
                  const canCheck = checkable.includes(n.level);
                  const hasKids = !!(n.children && n.children.length);
                  const active = trail[ci] === String(n.id);
                  const checked = ids.includes(String(n.id));
                  const under = selectedUnder.get(String(n.id)) ?? 0;
                  const partial = !checked && under > 0;
                  const underTitle = `${under} selected inside ${n.name}`;
                  return (
                    <div
                      key={n.id}
                      onClick={() => drill(ci, n)}
                      className={`flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-surface-2 ${active ? 'bg-brand-500/10' : ''}`}
                    >
                      {canCheck ? (
                        <button
                          type="button" disabled={disabled} className="shrink-0"
                          title={partial ? underTitle : undefined}
                          onClick={(e) => { e.stopPropagation(); toggle(String(n.id)); }}
                        >
                          <CheckBox checked={checked} partial={partial} />
                        </button>
                      ) : partial ? (
                        // Navigation-only level (above this role's reach) — it still has to show that
                        // a binding lives underneath it.
                        <span className="shrink-0" title={underTitle}><CheckBox checked={false} partial /></span>
                      ) : <span className="w-4 shrink-0" />}
                      <span className="flex min-w-0 flex-1 items-baseline gap-1">
                        <span className="shrink-0 whitespace-nowrap font-medium tabular-nums text-content">{n.code}</span>
                        {n.name && <span className="truncate text-subtle">· {n.name}</span>}
                      </span>
                      {under > 0 && (
                        <span title={underTitle} className="shrink-0 rounded-pill bg-brand-500/12 px-1.5 py-px text-2xs font-semibold tabular-nums text-brand-700 dark:text-brand-300">{under}</span>
                      )}
                      {hasKids && <ChevronRight className={`h-4 w-4 shrink-0 ${active ? 'text-brand-600' : 'text-subtle'}`} />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Selected chips + the ⭐ primary control (SP only). */}
      <div className="border-t border-line px-3 py-2">
        {ids.length === 0 ? (
          <span className="text-xs text-subtle">No territories selected{isSp ? ' — pick a district or beat' : ''}.</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {ids.map((id) => {
              const n = byId.get(id);
              if (!n) return null;
              const owns = primaryIds.includes(id);
              return (
                <span key={id} className={`inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-xs ${owns ? 'border-harvest-400/50 bg-harvest-400/12' : 'border-line bg-surface-2'}`}>
                  {canOwn && (
                    <button
                      type="button" disabled={disabled} onClick={() => togglePrimary(id)}
                      title={owns ? 'Owns new customers here — click to only cover it' : 'Make this the owner of this territory'}
                    >
                      <Star className={`h-3.5 w-3.5 ${owns ? 'fill-harvest-400 text-harvest-500' : 'text-subtle hover:text-harvest-500'}`} />
                    </button>
                  )}
                  <Pill color={LEVEL_PILL[n.level]}>{LEVEL_LABEL[n.level]}</Pill>
                  <span className="font-medium tabular-nums text-content">{n.code}</span>
                  <span className="text-subtle">{n.name}</span>
                  <button type="button" disabled={disabled} onClick={() => toggle(id)} className="ml-0.5 text-subtle hover:text-danger-500"><X className="h-3.5 w-3.5" /></button>
                </span>
              );
            })}
          </div>
        )}
        {(isSp || isSh) && ids.length > 0 && (
          <div className="mt-1 text-2xs text-muted">
            ★ = {isSh ? 'territories this Sales Head owns' : 'territories this Sales Person owns'} (new customers there route to them).
            {isSh ? ' Un-star any he only oversees.' : ' Un-star any they only cover.'}
          </div>
        )}
      </div>
    </div>
  );
}
